import { render } from 'preact'

import type {
  CookieCategory,
  CookieRecord,
} from '../../../services/cookieTranslationService/types'

import type {
  CookieConsentUiController,
  CookieConsentUiVisibility,
} from './types'

import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import { createCookieConsentController } from '#src/assets/ui/mount/cookieConsentController.ts'
import type { CookieConsentEngine } from '#src/assets/ui/mount/cookieConsentEngine.ts'
import { createShadowMount } from '#src/assets/ui/mount/createShadowMount.ts'
import { CookieConsentWidget } from '#src/assets/ui/widget/CookieConsentWidget.tsx'
import type { SiteConfig } from '#src/models/site-configs/siteConfigs.ts'

export function mountCookieConsentUi({
  t,
  locale,
  siteConfig,
  cookiesByCategory,
  engine,
  log,
}: {
  t: CookieConsentTranslator
  locale: string
  siteConfig: SiteConfig
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  engine: CookieConsentEngine
  log: (message: string, ...args: unknown[]) => void
}): {
  host: HTMLElement
  controller: CookieConsentUiController
} {
  const { host, container } = createShadowMount()

  const visibility: CookieConsentUiVisibility = {
    bannerVisible: false,
    settingsDialogVisible: false,
    preferencesModalVisible: false,
    actionButtonVisible: false,
  }

  const rerenderRef: { current: () => void } = { current: () => {} }

  const controller = createCookieConsentController({
    visibility,
    rerender: () => rerenderRef.current(),
    engine,
    log,
  })

  render(
    <CookieConsentWidget
      t={t}
      locale={locale}
      siteConfig={siteConfig}
      cookiesByCategory={cookiesByCategory}
      engine={engine}
      visibility={visibility}
      registerRerender={fn => {
        rerenderRef.current = fn
      }}
      onOpenSettingsDialog={() => {
        controller.showSettingsDialog()
      }}
      onHideSettingsDialog={() => {
        controller.hideSettingsDialog()
      }}
      onShowPreferencesModal={() => {
        controller.showPreferencesModal()
      }}
      onHidePreferencesModal={() => {
        controller.hidePreferencesModal()
      }}
      onConsentSaved={() => {
        controller.onConsentSaved()
      }}
    />,
    container,
  )

  document.addEventListener('click', event => {
    const composedPath = event.composedPath()
    const isInsideCustomUi = composedPath.includes(host)

    if (isInsideCustomUi || !engine.hasValidConsent()) {
      return
    }

    // Without valid consent the banner must stay visible until the user makes a choice.
    if (controller.isBannerVisible()) {
      controller.hideBanner()
      controller.showActionButton()
      return
    }

    if (controller.isSettingsDialogVisible()) {
      controller.hideSettingsDialog()
    }
  })

  return { host, controller }
}
